import { useEffect, useRef, useState } from "react";
import { Copy, Check, Share2, Pencil, ArrowRight, Volume2, Square, Loader2, Languages, Star, Download } from "lucide-react";
import { toast } from "sonner";
import { stripMarkdown, API } from "@/lib/api";
import { downloadAudio } from "@/lib/voice";
import AttachmentChip from "@/components/AttachmentChip";

const stripInvisibleAIControls = (value = "") =>
  String(value).replace(/[\u200B\u2063\u2064]/g, "");

/**
 * If the backend exhausted its auto-continuation budget it appends a tail
 * notice like:
 *   "— I've reached the maximum length I can deliver in one reply
 *    (answered through item 20). To continue, ask me for the next batch..."
 *
 * Detect that and surface a one-tap "Continue from N" button. Returns the
 * suggested next item number (e.g. 21) or "where-you-stopped" if the answer
 * wasn't a numbered list.
 */
function detectContinuationOffer(content) {
  if (!content) return null;
  if (!/I've reached the maximum length I can deliver in one reply/i.test(content)) {
    return null;
  }
  const m = content.match(/answered through item\s+(\d{1,4})/i);
  if (m) {
    const next = parseInt(m[1], 10) + 1;
    if (Number.isFinite(next) && next > 0 && next <= 9999) {
      return { next, isNumbered: true };
    }
  }
  return { next: null, isNumbered: false };
}

/**
 * One chat bubble. Two variants by role.
 *
 * - user: right-aligned, IBM Plex Sans, soft elevated pill. May carry attachments.
 *   Hovering reveals an Edit button that re-opens the message in the composer.
 * - assistant: left-aligned, Cormorant Garamond serif prose, no pill.
 */
export default function ChatMessage({ role, content, attachments, streaming = false, testId, onShare, onEdit, onContinue, convId, msgIndex, starred: initialStarred = false, canStar = false }) {
  const cleanContent = stripInvisibleAIControls(content || "");
  const [copied, setCopied] = useState(false);
  // Star signal — signed-in users flag a reply as excellent. Feeds the
  // admin curation panel as a "starred by N users" indicator. Free/
  // anonymous users don't see the button at all (canStar=false).
  const [starred, setStarred] = useState(!!initialStarred);
  const [starBusy, setStarBusy] = useState(false);
  // Listen state: idle → loading (fetching audio from Sarvam) → playing (audio
  // element is running) → idle (finished or stopped).
  const [ttsState, setTtsState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  // Cache the last generated TTS blob so we can offer a "Download
  // audio" button without re-fetching from /api/tts. Cleared whenever
  // the translation state changes so a user hearing Hindi doesn't
  // accidentally download the English version.
  const [ttsBlob, setTtsBlob] = useState(null);

  const onStar = async () => {
    if (starBusy || !canStar || !convId || typeof msgIndex !== "number") return;
    const next = !starred;
    setStarred(next);
    setStarBusy(true);
    try {
      const resp = await fetch(`${API}/curation/star`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conv_id: convId, index: msgIndex, starred: next }),
      });
      if (!resp.ok) throw new Error(`star failed (${resp.status})`);
    } catch (err) {
      setStarred(!next);  // roll back optimistic flip
      toast.error(err.message || "Couldn't save star.");
    } finally {
      setStarBusy(false);
    }
  };
  // Translation state — { code, name, text } or null. When set, we render
  // the translated text INSTEAD OF the original and expose a "Show
  // original" button to flip back. Language picker sits in a small
  // popover above the Translate action.
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const translateMenuRef = useRef(null);
  useEffect(() => {
    if (!translateOpen) return;
    const onDoc = (e) => {
      if (translateMenuRef.current && !translateMenuRef.current.contains(e.target)) {
        setTranslateOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [translateOpen]);

  const onTranslate = async (code, name) => {
    setTranslateOpen(false);
    if (translating) return;
    setTranslating(true);
    try {
      const resp = await fetch(`${API}/translate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: stripMarkdown(cleanContent || ""),
          target_language: code,
        }),
      });
      if (!resp.ok) {
        let msg = `Translation failed (${resp.status})`;
        try {
          const j = await resp.json();
          if (j?.detail) msg = j.detail;
        } catch { /* not JSON */ }
        throw new Error(msg);
      }
      const j = await resp.json();
      setTranslation({ code, name, text: j.translated, truncated: !!j.truncated });
    } catch (err) {
      toast.error(err.message || "Couldn't translate.");
    } finally {
      setTranslating(false);
    }
  };

  const onListen = async () => {
    // If already playing, treat the click as Stop — pause + release blob URL.
    if (ttsState === "playing" && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setTtsState("idle");
      return;
    }
    if (ttsState === "loading") return;
    setTtsState("loading");
    try {
      // If a translation is active, voice the translated text in the
      // target language — hearing HBM's read in Hindi/Tamil/etc is the
      // point of pairing Listen with Translate.
      const bodyText = translation ? translation.text : stripMarkdown(cleanContent || "");
      const bodyLang = translation ? { language: translation.code } : {};
      const resp = await fetch(`${API}/tts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: bodyText, ...bodyLang }),
      });
      if (!resp.ok) {
        let msg = `TTS failed (${resp.status})`;
        try {
          const j = await resp.json();
          if (j?.detail) msg = j.detail;
        } catch { /* not JSON */ }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      // Keep a reference so the Download button next to Listen has
      // something to save without another /tts round-trip. Same blob
      // powers both playback and download.
      setTtsBlob(blob);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setTtsState("idle");
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setTtsState("idle");
        URL.revokeObjectURL(url);
        audioRef.current = null;
        toast.error("Playback failed.");
      };
      setTtsState("playing");
      await audio.play();
    } catch (err) {
      setTtsState("idle");
      toast.error(err.message || "Couldn't generate voice.");
    }
  };
  const isUser = role === "user";

  const onCopy = async () => {
    if (!cleanContent) return;
    try {
      await navigator.clipboard.writeText(cleanContent);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1300);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (isUser) {
    const hasAttachments = attachments && attachments.length > 0;
    return (
      <div className="flex justify-end fade-up group" data-testid={testId}>
        <div className="max-w-[80%] md:max-w-[70%] flex flex-col items-end gap-2">
          {hasAttachments && (
            <div className="flex flex-wrap justify-end" data-testid={`${testId}-attachments`}>
              {attachments.map((a, i) => (
                <AttachmentChip
                  key={i}
                  attachment={a}
                  variant="compact"
                  testId={`${testId}-attachment-${i}`}
                />
              ))}
            </div>
          )}
          {cleanContent && (
            <div
              className="px-4 py-3 rounded-2xl rounded-br-md bg-[var(--bg-pill)] border border-[var(--border-soft)] font-ui text-[15px] md:text-base leading-relaxed whitespace-pre-wrap text-[var(--text)]"
              data-testid={`${testId}-content`}
            >
              {cleanContent}
            </div>
          )}
          {(onEdit || cleanContent) && (cleanContent || hasAttachments) && (
            <div className="focus-within:opacity-100 flex flex-wrap items-center justify-end gap-x-5 gap-y-2">
              {cleanContent && (
                <button
                  onClick={onCopy}
                  className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] active:text-[var(--text)] inline-flex items-center gap-1.5 transition-colors"
                  data-testid={`${testId}-copy`}
                  aria-label={copied ? "Copied" : "Copy message"}
                  title="Copy message"
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              )}
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] active:text-[var(--text)] inline-flex items-center gap-1.5 transition-colors"
                  data-testid={`${testId}-edit`}
                  aria-label="Edit and resend"
                  title="Edit and resend"
                >
                  <Pencil size={11} />
                  Edit
                </button>
              )}
              {/* Listen — read the user's own question back in US-accented
                  TTS. Symmetric with the assistant's Listen so the whole
                  conversation can be replayed as audio. Same /api/tts
                  endpoint; blob is cached in ttsBlob for the sibling
                  Download button. */}
              {cleanContent && (
                <button
                  onClick={onListen}
                  disabled={ttsState === "loading"}
                  className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] active:text-[var(--text)] inline-flex items-center gap-1.5 transition-colors disabled:opacity-60 disabled:cursor-progress"
                  data-testid={`${testId}-listen`}
                  aria-label={
                    ttsState === "playing" ? "Stop"
                    : ttsState === "loading" ? "Loading voice"
                    : "Listen"
                  }
                  title="Listen to your question"
                >
                  {ttsState === "loading" ? <Loader2 size={11} className="animate-spin" />
                    : ttsState === "playing" ? <Square size={11} strokeWidth={2.5} fill="currentColor" />
                    : <Volume2 size={11} />}
                  {ttsState === "playing" ? "Stop" : ttsState === "loading" ? "Loading" : "Listen"}
                </button>
              )}
              {/* Download — appears once Listen has generated the blob.
                  Same file naming pattern as HBM's download so query +
                  reply audio for the same turn pair cleanly on disk. */}
              {ttsBlob && (
                <button
                  onClick={() =>
                    downloadAudio(ttsBlob, {
                      role: "you",
                      label: (cleanContent || "").slice(0, 40),
                    })
                  }
                  className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] active:text-[var(--text)] inline-flex items-center gap-1.5 transition-colors"
                  data-testid={`${testId}-download-audio`}
                  aria-label="Download audio"
                  title="Save the audio you just heard"
                >
                  <Download size={11} />
                  Download
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // assistant
  const display = translation ? translation.text : stripMarkdown(cleanContent);
  const contOffer = !streaming ? detectContinuationOffer(cleanContent) : null;
  const contLabel = contOffer
    ? (contOffer.isNumbered && contOffer.next
        ? `Continue from ${contOffer.next}`
        : "Continue")
    : null;
  return (
    <div className="flex justify-start fade-up group" data-testid={testId}>
      <div className="max-w-full md:max-w-[90%] w-full">
        {translation && (
          <div
            className="mb-2 flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--accent)]"
            data-testid={`${testId}-translated-label`}
          >
            <Languages size={11} />
            Translated · {translation.name}
            <button
              onClick={() => setTranslation(null)}
              className="ml-auto underline decoration-dotted underline-offset-2 hover:no-underline"
              data-testid={`${testId}-show-original`}
            >
              Show original
            </button>
          </div>
        )}
        <div
          className="font-prose text-[19px] md:text-[22px] leading-[1.55] text-[var(--text)] whitespace-pre-wrap"
          data-testid={`${testId}-content`}
        >
          {display}
          {streaming && <span className="cursor-stream" aria-hidden="true" />}
        </div>

        {contOffer && onContinue && (
          <div className="mt-4">
            <button
              onClick={() => onContinue(contOffer)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--bg-pill)] border border-[var(--border-soft)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[11px] tracking-[0.18em] uppercase text-[var(--text)] transition-colors"
              data-testid={`${testId}-continue`}
              aria-label={contLabel}
              title="Resume the answer from where it stopped"
            >
              <ArrowRight size={12} />
              {contLabel}
            </button>
          </div>
        )}

        {!streaming && cleanContent && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`${testId}-actions`}>
            <button
              onClick={onCopy}
              className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] active:bg-white active:text-[var(--accent)] transition-colors"
              data-testid={`${testId}-copy`}
              aria-label={copied ? "Copied" : "Copy reply"}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copied" : "Copy"}
            </button>
            {onShare && (
              <button
                onClick={() => onShare(cleanContent)}
                className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] active:bg-white active:text-[var(--accent)] transition-colors"
                data-testid={`${testId}-share`}
                aria-label="Share reply"
              >
                <Share2 size={12} />
                Share
              </button>
            )}
            {canStar && (
              <button
                onClick={onStar}
                disabled={starBusy}
                className={`font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors disabled:opacity-60 ${
                  starred
                    ? "bg-white text-[var(--accent)] border-[var(--border)]"
                    : "border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)]"
                }`}
                data-testid={`${testId}-star`}
                aria-label={starred ? "Remove star" : "Star this reply"}
                title={starred ? "You starred this. Admins see it in curation." : "Mark this reply as excellent"}
              >
                <Star size={12} fill={starred ? "currentColor" : "none"} strokeWidth={2} />
                {starred ? "Starred" : "Star"}
              </button>
            )}
            {/* Listen — TTS via Sarvam Bulbul v3. Signed-in only; anon
                clicks 401. Playing state doubles as Stop. */}
            <button
              onClick={onListen}
              disabled={ttsState === "loading"}
              className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] active:bg-white active:text-[var(--accent)] disabled:opacity-60 disabled:cursor-progress transition-colors"
              data-testid={`${testId}-listen`}
              aria-label={
                ttsState === "playing" ? "Stop"
                : ttsState === "loading" ? "Loading voice"
                : "Listen"
              }
            >
              {ttsState === "loading" ? <Loader2 size={12} className="animate-spin" />
                : ttsState === "playing" ? <Square size={12} strokeWidth={2.5} fill="currentColor" />
                : <Volume2 size={12} />}
              {ttsState === "playing" ? "Stop" : ttsState === "loading" ? "Loading" : "Listen"}
            </button>
            {/* Download audio — appears once Listen has produced a blob.
                Downloads the exact audio the user just heard (respects
                the active translation, since we regenerate on lang
                change). No extra /tts round-trip. */}
            {ttsBlob && (
              <button
                onClick={() =>
                  downloadAudio(ttsBlob, {
                    role: "hbm",
                    label: (content || "").slice(0, 40),
                  })
                }
                className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] transition-colors"
                data-testid={`${testId}-download-audio`}
                aria-label="Download audio"
                title="Save the audio you just heard as an MP3"
              >
                <Download size={12} />
                Download
              </button>
            )}
            {/* Translate — Sarvam sarvam-translate:v1. Small popover
                picks the target language; on click we swap the displayed
                text and expose Show-original to flip back. Kept to 8
                languages for a clean menu on mobile. */}
            <div ref={translateMenuRef} className="relative">
              <button
                onClick={() => setTranslateOpen((v) => !v)}
                disabled={translating}
                className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] active:bg-white active:text-[var(--accent)] disabled:opacity-60 disabled:cursor-progress transition-colors"
                data-testid={`${testId}-translate`}
                aria-haspopup="listbox"
                aria-expanded={translateOpen}
              >
                {translating ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
                {translating ? "Translating" : translation ? translation.name : "Translate"}
              </button>
              {translateOpen && !translating && (
                <div
                  role="listbox"
                  className="absolute bottom-[calc(100%+8px)] left-0 min-w-[180px] max-h-[280px] overflow-y-auto bg-[var(--text)] border border-[var(--border)] rounded-xl shadow-2xl z-50"
                  data-testid={`${testId}-translate-menu`}
                >
                  {[
                    // Indic — routed to Sarvam (best-in-class Indic voice)
                    ["hi-IN", "Hindi"],
                    ["ta-IN", "Tamil"],
                    ["te-IN", "Telugu"],
                    ["ml-IN", "Malayalam"],
                    ["kn-IN", "Kannada"],
                    ["bn-IN", "Bengali"],
                    ["mr-IN", "Marathi"],
                    ["gu-IN", "Gujarati"],
                    ["pa-IN", "Punjabi"],
                    // Global — routed to LLM (DeepSeek/Claude)
                    ["es",    "Spanish"],
                    ["fr",    "French"],
                    ["de",    "German"],
                    ["pt",    "Portuguese"],
                    ["it",    "Italian"],
                    ["ru",    "Russian"],
                    ["zh",    "Chinese"],
                    ["ja",    "Japanese"],
                    ["ko",    "Korean"],
                    ["ar",    "Arabic"],
                    ["tr",    "Turkish"],
                    ["vi",    "Vietnamese"],
                    ["id",    "Indonesian"],
                    ["th",    "Thai"],
                    ["nl",    "Dutch"],
                  ].map(([code, name]) => (
                    <button
                      key={code}
                      role="option"
                      onClick={() => onTranslate(code, name)}
                      className="w-full text-left px-3 py-2 text-[var(--text)] text-[11px] font-mono uppercase tracking-[0.2em] hover:bg-[var(--text)]/10 transition-colors"
                      data-testid={`${testId}-translate-${code}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function TypingIndicator() {
  return (
    <div className="flex justify-start fade-up" data-testid="typing-indicator">
      <div className="flex items-center px-1 py-2">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
    </div>
  );
}
